import { addRxPlugin, createRxDatabase } from 'rxdb/plugins/core';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { Value } from 'typebox/value';
import { isRecord } from '@tadpoles/common';
import { serializePersistenceKey } from './primary-key.js';
import { fromRxDbDocument, mapRxDbFieldName, toRxDbDocument } from './rxdb-reserved-fields.js';
import { createRxDbSchema } from './rxdb-schema.js';
let hasInstalledRxDbQueryBuilderPlugin = false;
export class RxDbPersistenceCollection {
    #collection;
    #encode;
    #decode;
    #indexes;
    #primaryKey;
    constructor(options) {
        this.#collection = options.collection;
        this.#encode = options.encode;
        this.#decode = options.decode;
        this.#indexes = new Set(options.indexes);
        this.#primaryKey = options.primaryKey;
    }
    async get(id) {
        const document = await this.#collection.findOne(this.#serializeKey(id)).exec();
        return document ? this.#decode(document.toMutableJSON()) : null;
    }
    async findAll() {
        const documents = await this.#collection.find().exec();
        return documents.map((document) => this.#decode(document.toMutableJSON()));
    }
    async findByIndex(index, value) {
        this.#assertIndex(index);
        const documents = await this.#collection
            .find()
            .where(this.#serializeIndex(index))
            .eq(value)
            .exec();
        return documents.map((document) => this.#decode(document.toMutableJSON()));
    }
    async query(query) {
        const documents = await this.#buildQuery(query).exec();
        return documents.map((document) => this.#decode(document.toMutableJSON()));
    }
    async put(value) {
        await this.#collection.upsert(this.#encode(value));
    }
    async bulkPut(values) {
        const result = await this.#collection.bulkUpsert(values.map((value) => this.#encode(value)));
        if (result.error.length > 0) {
            throw new Error(`Failed to bulk upsert ${result.error.length} persistence documents`);
        }
    }
    async remove(id) {
        const document = await this.#collection.findOne(this.#serializeKey(id)).exec();
        if (document) {
            await document.remove();
        }
    }
    #serializeKey(id) {
        return serializePersistenceKey(this.#primaryKey, id);
    }
    #serializeIndex(index) {
        return mapRxDbFieldName(index);
    }
    #assertIndex(index) {
        if (!this.#indexes.has(index)) {
            throw new Error(`Persistence collection index "${index}" is not declared`);
        }
    }
    #isOperatorCondition(condition) {
        return typeof condition === 'object' && condition !== null && !Array.isArray(condition);
    }
    #buildQuery(query) {
        let rxQuery = this.#collection.find();
        if (query.selector) {
            for (const [field, condition] of Object.entries(query.selector)) {
                this.#assertIndex(field);
                const serializedField = this.#serializeIndex(field);
                if (this.#isOperatorCondition(condition)) {
                    for (const [operator, value] of Object.entries(condition)) {
                        if (value === undefined) {
                            continue;
                        }
                        rxQuery = this.#applyOperator(rxQuery, serializedField, operator, value);
                    }
                    continue;
                }
                rxQuery = rxQuery.where(serializedField).eq(condition);
            }
        }
        if (query.sort) {
            for (const { direction, field } of query.sort) {
                this.#assertIndex(field);
                const serializedField = this.#serializeIndex(field);
                rxQuery = rxQuery.sort(direction === 'desc' ? `-${serializedField}` : serializedField);
            }
        }
        if (query.skip !== undefined) {
            rxQuery = rxQuery.skip(query.skip);
        }
        if (query.limit !== undefined) {
            rxQuery = rxQuery.limit(query.limit);
        }
        return rxQuery;
    }
    #applyOperator(rxQuery, field, operator, value) {
        const queryBuilder = rxQuery.where(field);
        if (Array.isArray(value)) {
            if (operator === '$in') {
                return queryBuilder.in(value);
            }
            throw new Error(`Persistence query operator "${operator}" does not support array values`);
        }
        switch (operator) {
            case '$eq':
                return queryBuilder.eq(value);
            case '$gt':
                return queryBuilder.gt(value);
            case '$gte':
                return queryBuilder.gte(value);
            case '$lt':
                return queryBuilder.lt(value);
            case '$lte':
                return queryBuilder.lte(value);
            default:
                throw new Error(`Persistence query operator "${operator}" is not supported`);
        }
    }
}
export class RxDbPersistenceDatabase {
    #database;
    #scope;
    #collections = new Map();
    #collectionDefinitions = new Map();
    constructor(database, scope) {
        this.#database = database;
        this.#scope = scope;
    }
    async close() {
        await this.#database.close();
    }
    async getCollection(definition) {
        if (definition.scope !== this.#scope) {
            throw new Error(`Persistence collection "${definition.kind}" has scope "${definition.scope}" but database scope is "${this.#scope}"`);
        }
        this.#assertCollectionDefinitionCompatibility(definition);
        const collection = await this.#getOrCreateCollection(definition);
        if (this.#hasCustomEncodeCodec(definition)) {
            return new RxDbPersistenceCollection({
                collection,
                encode: (value) => this.#encodeDocument(definition, value),
                decode: (value) => this.#decodeDocument(definition, value),
                indexes: definition.indexes ?? [],
                primaryKey: definition.primaryKey
            });
        }
        if (this.#hasCustomDecodeCodec(definition)) {
            return new RxDbPersistenceCollection({
                collection,
                encode: (value) => this.#encodeIdentityDocument(definition, value),
                decode: (value) => this.#decodeDecodedDocument(definition, value),
                indexes: definition.indexes ?? [],
                primaryKey: definition.primaryKey
            });
        }
        return new RxDbPersistenceCollection({
            collection,
            encode: (value) => this.#encodeIdentityDocument(definition, value),
            decode: (value) => this.#decodeIdentityDocument(definition, value),
            indexes: definition.indexes ?? [],
            primaryKey: definition.primaryKey
        });
    }
    async #getOrCreateCollection(definition) {
        const collectionName = definition.kind;
        const existing = this.#collections.get(collectionName);
        if (existing) {
            return await existing;
        }
        const collectionPromise = this.#createCollection(definition);
        this.#collections.set(collectionName, collectionPromise);
        try {
            return await collectionPromise;
        }
        catch (error) {
            this.#collections.delete(collectionName);
            this.#collectionDefinitions.delete(collectionName);
            throw error;
        }
    }
    async #createCollection(definition) {
        const collectionName = definition.kind;
        this.#collectionDefinitions.set(collectionName, this.#serializeCollectionDefinition(definition));
        const existing = this.#database.collections[collectionName];
        if (existing) {
            return existing;
        }
        const collections = await this.#database.addCollections({
            [collectionName]: {
                schema: createRxDbSchema(definition)
            }
        });
        return collections[collectionName];
    }
    #encodeDocument(definition, value) {
        const encodedValue = definition.encode(value);
        if (!isRecord(encodedValue)) {
            throw new Error(`Persistence collection "${definition.kind}" encode() must return an object`);
        }
        return toRxDbDocument(encodedValue);
    }
    #decodeDocument(definition, value) {
        return definition.decode(Value.Decode(definition.modelSchema, fromRxDbDocument(value)));
    }
    #encodeIdentityDocument(definition, value) {
        if (!isRecord(value)) {
            throw new Error(`Persistence collection "${definition.kind}" values must be objects`);
        }
        return toRxDbDocument(value);
    }
    #decodeIdentityDocument(definition, value) {
        return Value.Decode(definition.modelSchema, fromRxDbDocument(value));
    }
    #decodeDecodedDocument(definition, value) {
        return definition.decode(Value.Decode(definition.modelSchema, fromRxDbDocument(value)));
    }
    #hasCustomEncodeCodec(definition) {
        return definition.encode !== undefined && definition.decode !== undefined;
    }
    #hasCustomDecodeCodec(definition) {
        return definition.decode !== undefined;
    }
    #assertCollectionDefinitionCompatibility(definition) {
        const collectionName = definition.kind;
        const existingDefinition = this.#collectionDefinitions.get(collectionName);
        if (!existingDefinition) {
            return;
        }
        const currentDefinition = this.#serializeCollectionDefinition(definition);
        if (existingDefinition !== currentDefinition) {
            throw new Error(`Persistence collection "${collectionName}" is already registered with a different definition`);
        }
    }
    #serializeCollectionDefinition(definition) {
        return JSON.stringify({
            collectionName: definition.kind,
            scope: definition.scope,
            primaryKey: definition.primaryKey,
            indexes: definition.indexes ?? [],
            schema: createRxDbSchema(definition)
        });
    }
}
export class RxDbPersistenceManager {
    #databaseNamePrefix;
    #multiInstance;
    #storage;
    #databases = new Map();
    constructor(options) {
        RxDbPersistenceManager.#ensureRxDbQueryBuilderPlugin();
        this.#databaseNamePrefix = options.databaseNamePrefix;
        this.#multiInstance = options.multiInstance ?? false;
        this.#storage = options.storage ?? getRxStorageDexie();
    }
    async global() {
        return await this.#getDatabase('global');
    }
    async forUser(scopeId) {
        return await this.#getDatabase(`user:${scopeId}`);
    }
    async close() {
        const databases = await Promise.all(this.#databases.values());
        await Promise.all(databases.map(async (database) => {
            await database.close();
        }));
        this.#databases.clear();
    }
    async #getDatabase(scopeKey) {
        const existing = this.#databases.get(scopeKey);
        if (existing) {
            return await existing;
        }
        const databasePromise = this.#createDatabase(scopeKey);
        this.#databases.set(scopeKey, databasePromise);
        return await databasePromise;
    }
    async #createDatabase(scopeKey) {
        const scope = scopeKey === 'global' ? 'global' : 'user';
        const name = this.#createDatabaseName(scopeKey);
        const database = await createRxDatabase({
            name,
            storage: this.#storage,
            multiInstance: this.#multiInstance,
            eventReduce: true
        });
        return new RxDbPersistenceDatabase(database, scope);
    }
    #createDatabaseName(scopeKey) {
        if (scopeKey === 'global') {
            return `${this.#databaseNamePrefix}-global`;
        }
        return `${this.#databaseNamePrefix}-${this.#encodeScopeKey(scopeKey)}`;
    }
    #encodeScopeKey(scopeKey) {
        return Array.from(scopeKey)
            .map((character) => character.charCodeAt(0).toString(16).padStart(4, '0'))
            .join('');
    }
    static #ensureRxDbQueryBuilderPlugin() {
        if (hasInstalledRxDbQueryBuilderPlugin) {
            return;
        }
        addRxPlugin(RxDBQueryBuilderPlugin);
        hasInstalledRxDbQueryBuilderPlugin = true;
    }
}
